import React from "react";
import { Box, CircularProgress, Typography, Paper, Fade } from "@mui/material";
import SyncIcon from "@mui/icons-material/Sync";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

/**
 * Real-time progress indicator that displays in the bottom right corner
 * @param {Object} props - Component props
 * @param {number} props.progress - Current progress (0-100)
 * @param {boolean} props.isVisible - Whether the indicator is visible
 * @param {Object} props.error - Error object if any
 */
const ProgressIndicator = ({
  progress = 0,
  isVisible = false,
  error = null,
}) => {
  // Determine the status and icon
  const isComplete = progress >= 100;
  const hasError = error !== null;

  // Don't render if not visible
  if (!isVisible) return null;

  return (
    <Fade in={isVisible}>
      <Paper
        elevation={3}
        sx={{
          position: "fixed",
          bottom: "1.5rem",
          right: "5rem", // Position to not overlap with the Add button
          borderRadius: "8px",
          overflow: "hidden",
          zIndex: 1000,
          display: "flex",
          alignItems: "center",
          padding: "0.5rem 1rem",
          backgroundColor: hasError
            ? "#fee2e2"
            : isComplete
            ? "#d1fae5"
            : "#fff",
          maxWidth: "16rem",
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
        }}
      >
        <Box
          sx={{
            position: "relative",
            display: "flex",
            alignItems: "center",
            mr: 1.5,
          }}
        >
          {!isComplete && !hasError && (
            <CircularProgress
              variant="determinate"
              value={progress}
              size={32}
              thickness={4}
              sx={{ color: "#fdd34d" }}
            />
          )}

          {isComplete && !hasError && (
            <CheckCircleIcon sx={{ color: "#10b981", fontSize: 28 }} />
          )}

          {hasError && (
            <SyncIcon
              sx={{
                color: "#ef4444",
                fontSize: 28,
                animation: "spin 1.5s linear infinite",
                "@keyframes spin": {
                  "0%": { transform: "rotate(0deg)" },
                  "100%": { transform: "rotate(360deg)" },
                },
              }}
            />
          )}
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              fontSize: "0.8125rem",
              color: hasError ? "#b91c1c" : isComplete ? "#065f46" : "#111827",
            }}
          >
            {hasError
              ? "Sync Error"
              : isComplete
              ? "Data Up to Date"
              : "Updating Data..."}
          </Typography>

          <Typography
            variant="caption"
            sx={{
              color: hasError ? "#b91c1c" : isComplete ? "#065f46" : "#6b7280",
              fontSize: "0.75rem",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {hasError
              ? error.message || "Failed to sync data"
              : isComplete
              ? "Real-time sync active"
              : `${progress}% complete`}
          </Typography>
        </Box>
      </Paper>
    </Fade>
  );
};

export default ProgressIndicator;
